/**
 * SunoLyricsButton — «Сгенерировать текст песни».
 *
 * Кнопка в audio-виджете. Клик открывает модал (desktop) / шторку
 * (mobile) — компонент `Sheet` сам переключается по viewport.
 *
 * 2 шага:
 *   Step 1 — тема + «Сгенерировать». Если в истории уже есть
 *            сохранённые тексты — дополнительная кнопка «Мои тексты»
 *            ведёт сразу на step 2 без новой генерации.
 *   Step 2 — две секции:
 *              a) «Новые варианты» — 2 только что сгенерированных
 *                 варианта, textarea редактируемые, раскрытые
 *              b) «Мои сохранённые тексты» — карточки-аккордеоны
 *                 со всеми прошлыми lyrics-треками юзера из БД
 *            Любой вариант можно:
 *              - отредактировать прямо в textarea (state локальный,
 *                не ломает соседние варианты)
 *              - использовать → отредактированный текст вставляется
 *                в поле промпта виджета
 *
 * Цена операции lyrics берётся из `Model.variants[key='lyrics']`
 * (приходит с бэка через useProviders). Показывается на кнопке и в
 * sheet-actions. Если variant не сконфигурирован на бэке — просто
 * не отображается, UI не ломается.
 */
import { useCallback, useEffect, useState } from 'react'
import { useCreditsGuard } from '../../../hooks/useCreditsGuard'
import CreditsShortNote from '../../../components/CreditsShortNote'
import { createPortal } from 'react-dom'
import { extendLyrics, generateLyrics, getUserTracks, type SunoTrack } from '../../../api/music'
import { showToast } from '../../../components/Toast'
import { providerOf } from '../utils/trackOperations'
import Sheet from '../../../components/Sheet'
import SpotlightTour from '../../../components/SpotlightTour'
import { useSunoPolling } from '../hooks/useSunoPolling'
import TokenPrice from '../../../components/TokenPrice'
import { PreloaderDynamic } from '../../../components/Preloader'
import {
	buildLyricsSheetTourSteps,
	SUNO_LYRICS_SHEET_TOUR_KEY,
} from '../../../data/widgetTourSteps'
import type { Model } from '../../../data/models'

interface Props {
	model: Model | null
	/** Значение поля «Название трека» в виджете — предзаполняет поле
	 *  «Название песни» в step 1 модалки (юзер может изменить). */
	currentTitle: string
	/** Вставляет выбранный вариант в виджет:
	 *  - title → в поле «Название трека» (settings.audioTitle).
	 *    Передаём ТОЛЬКО если юзер указал название в модалке.
	 *  - lyrics → в поле промпта (через setPrompt) */
	onApply: (result: { title: string; lyrics: string }) => void
	disabled?: boolean
}

type Step = 1 | 2

// ── Лимит Suno API для prompt'а лирики ────────────────────
// Suno /lyrics принимает только {prompt, callBackUrl} и ВСЕГДА режет prompt
// по 200 символов. У этого endpoint'а нет режима customMode (это параметр
// /generate, не /lyrics) — поэтому лимит фиксированный, не зависит от тоггла
// «Кастомный режим» в основном виджете. Фронт валидирует ДО отправки, бэк
// дублирует. https://docs.sunoapi.org/suno-api/generate-lyrics
const LYRICS_LIMIT = 200
// Mureka /lyrics длиннее (адаптер каплит prompt 1024) — не режем тему
// по Suno-лимиту, когда выбрана Mureka.
const MUREKA_LYRICS_LIMIT = 1024

// ── LocalStorage кэш для связи trackId → title ──────────────
// Suno API /lyrics не принимает title, поэтому мы сохраняем его
// локально для каждого сгенерированного трека. Это позволяет
// показывать в истории названия песен которые юзер указал при
// генерации, даже если бэк их не хранит.
const TITLES_CACHE_KEY = 'suno_lyrics_titles_v1'

function loadTitlesCache(): Record<string, string> {
	try {
		const raw = localStorage.getItem(TITLES_CACHE_KEY)
		if (!raw) return {}
		const parsed = JSON.parse(raw)
		return typeof parsed === 'object' && parsed !== null ? parsed : {}
	} catch {
		return {}
	}
}

function saveTitlesCache(cache: Record<string, string>): void {
	try {
		localStorage.setItem(TITLES_CACHE_KEY, JSON.stringify(cache))
	} catch {
		// quota exceeded — игнорим
	}
}

/** Разрешает название трека: локальный кэш → поле бэка → пусто */
function resolveTrackTitle(track: SunoTrack, cache: Record<string, string>): string {
	return cache[track.id]?.trim() || track.title?.trim() || ''
}

// ── Helpers ──────────────────────────────────────────────────

/** Заголовок для карточки истории.
 *  Resolve-порядок: localStorage cache → track.title → «(не указано)». */
function getCardTitle(track: SunoTrack, cache: Record<string, string>): string {
	const resolved = resolveTrackTitle(track, cache)
	return resolved || '(не указано)'
}

/** Форматирует created_at в короткий вид */
function formatDate(iso: string | null): string {
	if (!iso) return ''
	const date = new Date(iso)
	if (isNaN(date.getTime())) return ''
	const now = new Date()
	const isSameDay =
		date.getDate() === now.getDate() &&
		date.getMonth() === now.getMonth() &&
		date.getFullYear() === now.getFullYear()
	if (isSameDay) {
		return `Сегодня в ${date.toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}`
	}
	return date.toLocaleDateString('ru', { day: 'numeric', month: 'short' })
}

// ── Component ────────────────────────────────────────────────

export default function SunoLyricsButton({
	model,
	currentTitle,
	onApply,
	disabled,
}: Props) {
	const guard = useCreditsGuard()
	const { status, error, run, reset } = useSunoPolling()
	const [sheetOpen, setSheetOpen] = useState(false)
	const [step, setStep] = useState<Step>(1)
	// Tour-стейт: авто-показ на первом открытии sheet'а, + ручной перезапуск
	// через `?` help-кнопку в header'е (handleRestartTour).
	const [tourOpen, setTourOpen] = useState(false)

	// Два отдельных поля в step 1:
	// title — название песни (opt), улетит обратно в audioTitle виджета
	// topic — описание темы (obligatory), уйдёт в API как prompt
	const [title, setTitle] = useState('')
	const [topic, setTopic] = useState('')

	// История всех lyrics-треков юзера (limit 20)
	const [allHistory, setAllHistory] = useState<SunoTrack[]>([])
	// id только что сгенерированных в этой сессии — выделяем их
	// отдельной секцией «Новые варианты» сверху step 2
	const [currentIds, setCurrentIds] = useState<Set<string>>(new Set())
	// Локальные правки юзера для каждого трека (override track.lyrics)
	const [editedLyrics, setEditedLyrics] = useState<Record<string, string>>({})
	// Какие карточки истории раскрыты
	const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
	// Кэш названий трека, сохраняется в localStorage. Загружается при
	// открытии модалки и обновляется при каждой новой генерации.
	const [titlesCache, setTitlesCache] = useState<Record<string, string>>({})

	const lyricsPrice = model?.variants?.find((v) => v.key === 'lyrics')?.credits_price ?? null
	// Цена подсказки строки: свой тариф, а пока он не заведён — цена текста
	// (ровно так же считает бэк, чтобы витрина не расходилась со списанием).
	const extendPrice =
		model?.variants?.find((v) => v.key === 'lyrics_extend')?.credits_price ?? lyricsPrice

	// Разделение ролей: текст песни пишет ТА нейросеть, что выбрана для
	// генерации. Mureka /lyrics принимает больше (адаптер каплит 1024) и
	// отвечает синхронно; Suno — жёсткие 200 и async. Лимит и формат модели
	// зависят от провайдера.
	const isMureka = (model?.id || '').toLowerCase().startsWith('mureka')
	const currentLimit = isMureka ? MUREKA_LYRICS_LIMIT : LYRICS_LIMIT

	// Загрузка истории при открытии (один раз)
	const loadHistory = useCallback(async () => {
		try {
			const { tracks } = await getUserTracks({ operation: 'lyrics', limit: 20 })
			setAllHistory(tracks.filter((t) => t.lyrics))
		} catch {
			// тихо — history просто будет пустой
		}
	}, [])

	// При открытии модалки:
	// 1. Предзаполняем поле «Название песни» из виджета (audioTitle)
	// 2. Грузим историю
	// 3. Читаем кэш titles из localStorage
	useEffect(() => {
		if (!sheetOpen) return
		if (!title.trim()) {
			setTitle(currentTitle.trim())
		}
		loadHistory()
		setTitlesCache(loadTitlesCache())
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sheetOpen])

	// Авто-показ тура при первом открытии sheet'а. 350ms delay чтобы Sheet
	// закончил animation и targets появились в DOM. Только для step=1, т.к.
	// все tour-шаги показывают элементы из step 1.
	useEffect(() => {
		if (!sheetOpen || step !== 1) return
		if (typeof window === 'undefined') return
		if (window.localStorage.getItem(SUNO_LYRICS_SHEET_TOUR_KEY)) return
		const t = setTimeout(() => setTourOpen(true), 350)
		return () => clearTimeout(t)
	}, [sheetOpen, step])

	const handleTourFinish = useCallback(() => {
		try {
			window.localStorage.setItem(SUNO_LYRICS_SHEET_TOUR_KEY, '1')
		} catch {
			// localStorage может быть отключён — тур просто покажется снова
		}
		setTourOpen(false)
	}, [])

	const handleRestartTour = useCallback(() => {
		// Форсим step=1, если юзер на шаге результата — там нет target'ов
		// из tour'а, запуск бесполезен. Переключаем обратно на step 1.
		setStep(1)
		setTourOpen(true)
	}, [])

	// ── Handlers ────────────────────────────────────────────

	const handleOpen = useCallback(() => {
		setSheetOpen(true)
		setStep(1)
	}, [])

	const handleClose = useCallback(() => {
		setSheetOpen(false)
		// Reset state после того как sheet закроется (чтобы не мигало).
		// Оставляем title/topic, чтобы при повторном открытии юзер
		// не вводил заново (пока сессия жива).
		setTimeout(() => {
			setStep(1)
			setCurrentIds(new Set())
			setEditedLyrics({})
			setExpandedIds(new Set())
			reset()
		}, 350)
	}, [reset])

	const handleGenerate = useCallback(async () => {
		const trimmed = topic.trim()
		if (!trimmed || status === 'running') return

		// Защита от обхода disabled: если каким-то образом prompt > 200,
		// не отправляем — просто тихо игнорируем. Кнопка должна быть
		// disabled в этом случае, но defensive check не повредит.
		if (trimmed.length > currentLimit) return

		// Сразу переходим на step 2 — там показываем loading, потом результат
		setStep(2)
		setCurrentIds(new Set()) // сбрасываем прошлую "новую" секцию

		// Модель шлём по провайдеру: Mureka — как есть (mureka-8 → бэк роутит
		// в Mureka /lyrics), Suno — в его формате (suno-v4_5all → V4_5ALL).
		const lyricsModel = isMureka
			? model?.id || 'auto'
			: model?.id?.replace('suno-', '').toUpperCase().replace(/-/g, '_') || 'V5_5'
		const result = await run(() =>
			generateLyrics({ prompt: trimmed, model: lyricsModel }),
		)

		if (result) {
			try {
				const { tracks } = await getUserTracks({ operation: 'lyrics', limit: 20 })
				const filtered = tracks.filter((t) => t.lyrics)
				const freshIds = filtered.slice(0, 2).map((t) => t.id)
				setCurrentIds(new Set(freshIds))
				setAllHistory(filtered)

				// Если юзер указал название — связываем его с новыми treck'ами
				// в локальном кэше. Т.к. API /lyrics не принимает title,
				// это единственный способ сохранить связь.
				const trimmedTitle = title.trim()
				if (trimmedTitle) {
					const next = { ...titlesCache }
					freshIds.forEach((id) => {
						next[id] = trimmedTitle
					})
					setTitlesCache(next)
					saveTitlesCache(next)
				}
			} catch {
				// fallback — без подтягивания истории
			}
		}
	}, [topic, title, titlesCache, status, run, currentLimit, isMureka, model])

	const handleBack = useCallback(() => {
		setStep(1)
		reset()
	}, [reset])

	/** Переход к step 2 без генерации — для просмотра истории */
	const handleOpenHistory = useCallback(() => {
		setStep(2)
		setCurrentIds(new Set()) // нет "новых", только история
	}, [])

	const handleEdit = useCallback((trackId: string, text: string) => {
		setEditedLyrics((prev) => ({ ...prev, [trackId]: text }))
	}, [])

	const handleUse = useCallback(
		(track: SunoTrack) => {
			const finalText = editedLyrics[track.id] ?? track.lyrics ?? ''
			if (!finalText.trim()) return
			// Название: resolve через кэш → track.title → пусто.
			// Пустая строка НЕ будет применяться к audioTitle виджета
			// (см. onApply в GenerateInputBar — проверяет truthy).
			const finalTitle = resolveTrackTitle(track, titlesCache)
			onApply({ title: finalTitle, lyrics: finalText })
			handleClose()
		},
		[editedLyrics, titlesCache, onApply, handleClose],
	)

	const toggleExpanded = useCallback((trackId: string) => {
		setExpandedIds((prev) => {
			const next = new Set(prev)
			if (next.has(trackId)) next.delete(trackId)
			else next.add(trackId)
			return next
		})
	}, [])

	// ── Derived ─────────────────────────────────────────────

	const currentVariants = allHistory.filter((t) => currentIds.has(t.id))
	const historyVariants = allHistory.filter((t) => !currentIds.has(t.id))
	const hasAnyHistory = allHistory.length > 0

	// Лимит prompt'а лирики зависит от провайдера (currentLimit определён
	// выше: Suno 200 / Mureka 1024). exceeded — превышение лимита,
	// используется для окраски счётчика, hint'а и disable submit-кнопки.
	const topicLen = topic.length
	const exceeded = topicLen > currentLimit
	// Кнопка submit disabled если: пустой topic ИЛИ превышен лимит
	const cannotGenerate = !topic.trim() || exceeded

	// ── Render ──────────────────────────────────────────────

	return (
		<>
			<button
				type="button"
				className="suno-lyrics-btn"
				onClick={handleOpen}
				disabled={disabled}
				title="Сгенерировать текст песни"
			>
				<span className="suno-lyrics-btn__text">Сгенерировать текст песни</span>
				{lyricsPrice !== null && (
					<span className="suno-lyrics-btn__price">
						<TokenPrice value={lyricsPrice} iconSize={10} />
					</span>
				)}
			</button>

			<Sheet
				open={sheetOpen}
				onClose={handleClose}
				variant="center"
				title="Сгенерировать текст песни"
				className="suno-lyrics-sheet"
				headerExtra={
					<button
						type="button"
						className="suno-lyrics-sheet__help"
						onClick={handleRestartTour}
						aria-label="Как пользоваться генерацией текста"
						title="Как пользоваться"
					>
						?
					</button>
				}
			>
				{/* Степпер-индикатор */}
				<div className="suno-stepper">
					<div
						className={`suno-stepper__step${step === 1 ? ' suno-stepper__step--active' : ''}${
							step > 1 ? ' suno-stepper__step--done' : ''
						}`}
					>
						<span className="suno-stepper__num">1</span>
						<span className="suno-stepper__label">Тема</span>
					</div>
					<div className="suno-stepper__line" />
					<div className={`suno-stepper__step${step === 2 ? ' suno-stepper__step--active' : ''}`}>
						<span className="suno-stepper__num">2</span>
						<span className="suno-stepper__label">Результат</span>
					</div>
				</div>

				{/* ── Step 1: название песни + тема ── */}
				{step === 1 && (
					<div className="suno-lyrics-sheet__body">
						{/* Название — optional, для связи с историей */}
						<label className="suno-lyrics-sheet__field-label" htmlFor="suno-title">
							Название песни <span className="suno-lyrics-sheet__field-opt">(необязательно)</span>
						</label>
						<input
							id="suno-title"
							type="text"
							className="suno-lyrics-sheet__title-input"
							placeholder="Например: Summer Vibes"
							value={title}
							onChange={(e) => setTitle(e.target.value)}
							maxLength={100}
						/>

						{/* Тема — обязательна, идёт в API как prompt */}
						<label
							className="suno-lyrics-sheet__field-label"
							htmlFor="suno-topic"
							style={{ marginTop: 14 }}
						>
							О чём песня?
						</label>
						<textarea
							id="suno-topic"
							className={`suno-lyrics-sheet__topic-input${
								exceeded ? ' suno-lyrics-sheet__topic-input--invalid' : ''
							}`}
							placeholder="Например: песня о море и свободе, настроение меланхоличное"
							value={topic}
							onChange={(e) => setTopic(e.target.value)}
							rows={4}
							maxLength={currentLimit}
							autoFocus
						/>

						{/* Счётчик символов под textarea. Красный при превышении
						    лимита провайдера (Suno 200 / Mureka 1024). */}
						<div className="suno-lyrics-sheet__limit-row suno-lyrics-sheet__limit-row--counter-only">
							<span
								className={`suno-lyrics-sheet__counter${
									exceeded ? ' suno-lyrics-sheet__counter--exceeded' : ''
								}`}
								aria-live="polite"
							>
								{topicLen} / {currentLimit}
							</span>
						</div>

						{/* Hint при превышении лимита провайдера. */}
						{exceeded ? (
							<div className="suno-lyrics-sheet__limit-hint suno-lyrics-sheet__limit-hint--error">
								Превышен лимит {currentLimit} символов — опишите тему песни
								короче. Текст сгенерируется по ней.
							</div>
						) : (
							<div className="suno-lyrics-sheet__hint">
								Опишите тему и настроение в пределах {currentLimit} символов — по
								ней сгенерируется полный текст песни.
							</div>
						)}

						{hasAnyHistory && (
							<button
								type="button"
								className="suno-lyrics-sheet__history-link"
								onClick={handleOpenHistory}
							>
								Мои сохранённые тексты ({allHistory.length}) →
							</button>
						)}
					</div>
				)}

				{/* ── Step 2: свежие + история ── */}
				{step === 2 && (
					<div className="suno-lyrics-sheet__body">
						{/* Loading: анимированный логотип «УмникAI» + текст-статус.
						    PreloaderDynamic — variant с бильярдной волной по часовой
						    стрелке (для генерации контента, не для idle-загрузки). */}
						{status === 'running' && (
							<div className="suno-lyrics-sheet__loading">
								<PreloaderDynamic size={140} className="suno-lyrics-sheet__loading-logo" />
								<div className="suno-lyrics-sheet__loading-text">Генерируем текст по теме…</div>
								<div className="suno-lyrics-sheet__hint">Обычно 10–60 секунд</div>
							</div>
						)}

						{status === 'failed' && (
							<div className="suno-lyrics-sheet__error">
								{error || 'Не удалось сгенерировать текст'}
							</div>
						)}

						{/* Новые варианты (свежие 2) */}
						{status !== 'running' && currentVariants.length > 0 && (
							<section className="suno-lyrics-sheet__section">
								<h3 className="suno-lyrics-sheet__section-title">Новые варианты</h3>
								{currentVariants.map((track, idx) => (
									<EditableVariant
										key={track.id}
										track={track}
										label={`Вариант ${idx + 1}`}
										date={formatDate(track.created_at)}
										edited={editedLyrics[track.id]}
										onEdit={(text) => handleEdit(track.id, text)}
										onUse={() => handleUse(track)}
										modelId={model?.id ?? null}
										extendPrice={extendPrice}
									/>
								))}
							</section>
						)}

						{/* Пустое состояние если ни свежих ни истории */}
						{status !== 'running' &&
							currentVariants.length === 0 &&
							historyVariants.length === 0 && (
								<div className="suno-lyrics-sheet__empty">
									Пока нет сгенерированных текстов.
									<br />
									Вернитесь назад и создайте первый.
								</div>
							)}

						{/* История */}
						{status !== 'running' && historyVariants.length > 0 && (
							<section className="suno-lyrics-sheet__section">
								<h3 className="suno-lyrics-sheet__section-title">Мои сохранённые тексты</h3>
								{historyVariants.map((track) => (
									<AccordionVariant
										key={track.id}
										track={track}
										title={getCardTitle(track, titlesCache)}
										date={formatDate(track.created_at)}
										expanded={expandedIds.has(track.id)}
										onToggle={() => toggleExpanded(track.id)}
										edited={editedLyrics[track.id]}
										onEdit={(text) => handleEdit(track.id, text)}
										onUse={() => handleUse(track)}
										modelId={model?.id ?? null}
										extendPrice={extendPrice}
									/>
								))}
							</section>
						)}
					</div>
				)}

				{/* Actions */}
				<div className="suno-lyrics-sheet__actions">
					{step === 1 && (
						<>
							<button
								type="button"
								className="suno-sheet-btn suno-sheet-btn--ghost"
								onClick={handleClose}
							>
								Отмена
							</button>
							<button
								type="button"
								className="suno-sheet-btn suno-sheet-btn--primary"
								onClick={handleGenerate}
								disabled={cannotGenerate || guard.shortFor(lyricsPrice)}
								title={
									lyricsPrice !== null && guard.shortFor(lyricsPrice) ? guard.titleFor(lyricsPrice) : exceeded
										? `Превышен лимит ${currentLimit} символов`
										: undefined
								}
							>
								Сгенерировать
								{lyricsPrice !== null && (
									<span className="suno-sheet-btn__price">
										<TokenPrice value={lyricsPrice} iconSize={11} />
									</span>
								)}
							</button>
							{guard.shortFor(lyricsPrice) && <CreditsShortNote price={lyricsPrice} />}
						</>
					)}

					{step === 2 && (
						<button
							type="button"
							className="suno-sheet-btn suno-sheet-btn--ghost"
							onClick={handleBack}
							disabled={status === 'running'}
						>
							← Новая генерация
						</button>
					)}
				</div>
			</Sheet>

			{/* Тур поверх sheet'а. Через createPortal к document.body чтобы
			    не прилипать к containing block'у (transform/filter на
			    ancestor'ах). Показываем только на step 1 — tour-шаги
			    таргетят элементы именно первого шага. */}
			{sheetOpen && step === 1 && tourOpen && createPortal(
				<SpotlightTour
					steps={buildLyricsSheetTourSteps(hasAnyHistory)}
					storageKey={SUNO_LYRICS_SHEET_TOUR_KEY}
					onFinish={handleTourFinish}
				/>,
				document.body,
			)}
		</>
	)
}

// ── Sub-components ───────────────────────────────────────────

/**
 * «Подсказать строку» — Mureka дописывает СЛЕДУЮЩУЮ строку к написанному
 * (её ручка отдаёт ровно одну, сколько контекста ни присылай). Поэтому
 * обещаем строку, а не куплет, и показываем цену: каждый клик платный.
 * У Suno такой операции нет — кнопка просто не появляется.
 */
function ExtendLineButton({
	value,
	modelId,
	price,
	onAppend,
}: {
	value: string
	modelId: string | null
	price: number | null
	onAppend: (line: string) => void
}) {
	const guard = useCreditsGuard()
	const [busy, setBusy] = useState(false)
	if (providerOf(modelId) !== 'mureka') return null

	const run = async () => {
		const base = value.trim()
		if (!base) {
			showToast('Сначала напишите хотя бы строку — её и продолжим', 'info')
			return
		}
		setBusy(true)
		try {
			const res = await extendLyrics({ lyrics: base, model: modelId || undefined })
			if (!res.ok || !res.lyrics) {
				showToast(res.error || 'Не удалось подсказать строку', 'error')
				return
			}
			onAppend(res.lyrics.trim())
		} finally {
			setBusy(false)
		}
	}

	return (
		<button
			type="button"
			className="suno-sheet-btn suno-sheet-btn--soft suno-sheet-btn--compact"
			onClick={run}
			disabled={busy || guard.shortFor(price)}
			title={price !== null && guard.shortFor(price) ? guard.titleFor(price) : 'Mureka предложит следующую строку по смыслу написанного'}
		>
			{busy ? 'Подбираем…' : 'Подсказать строку'}
			{!busy && price !== null && (
				<span className="suno-lyrics-sheet__btn-price">
					<TokenPrice value={price} iconSize={10} />
				</span>
			)}
		</button>
	)
}

/** Раскрытый редактируемый вариант — для секции «Новые варианты» */
function EditableVariant({
	track,
	label,
	date,
	edited,
	onEdit,
	onUse,
	modelId,
	extendPrice,
}: {
	track: SunoTrack
	label: string
	date: string
	edited: string | undefined
	onEdit: (text: string) => void
	onUse: () => void
	modelId: string | null
	extendPrice: number | null
}) {
	const value = edited ?? track.lyrics ?? ''
	const isDirty = edited !== undefined && edited !== track.lyrics

	return (
		<div className="suno-lyrics-sheet__variant suno-lyrics-sheet__variant--editable">
			<div className="suno-lyrics-sheet__variant-header">
				<span className="suno-lyrics-sheet__variant-label">
					{label}
					{isDirty && <em className="suno-lyrics-sheet__variant-dirty"> · изменён</em>}
				</span>
				<div className="suno-lyrics-sheet__variant-meta">
					{date && <span className="suno-lyrics-sheet__variant-date">{date}</span>}
					<button
						type="button"
						className="suno-sheet-btn suno-sheet-btn--primary suno-sheet-btn--compact"
						onClick={onUse}
					>
						Использовать
					</button>
				</div>
			</div>
			<textarea
				className="suno-lyrics-sheet__variant-textarea"
				value={value}
				onChange={(e) => onEdit(e.target.value)}
				rows={10}
			/>
			<div className="suno-lyrics-sheet__variant-tools">
				<ExtendLineButton
					value={value}
					modelId={modelId}
					price={extendPrice}
					onAppend={(line) => onEdit(`${value.replace(/\s+$/, '')}\n${line}`)}
				/>
			</div>
		</div>
	)
}

/** Аккордеон-карточка для истории */
function AccordionVariant({
	track,
	title,
	date,
	expanded,
	onToggle,
	edited,
	onEdit,
	onUse,
	modelId,
	extendPrice,
}: {
	track: SunoTrack
	title: string
	date: string
	expanded: boolean
	onToggle: () => void
	edited: string | undefined
	onEdit: (text: string) => void
	onUse: () => void
	modelId: string | null
	extendPrice: number | null
}) {
	const value = edited ?? track.lyrics ?? ''
	const isDirty = edited !== undefined && edited !== track.lyrics

	return (
		<div
			className={`suno-lyrics-sheet__card${expanded ? ' suno-lyrics-sheet__card--expanded' : ''}`}
		>
			<button
				type="button"
				className="suno-lyrics-sheet__card-header"
				onClick={onToggle}
				aria-expanded={expanded}
			>
				<span className="suno-lyrics-sheet__card-title">
					{title}
					{isDirty && <em className="suno-lyrics-sheet__variant-dirty"> · изменён</em>}
				</span>
				<span className="suno-lyrics-sheet__card-meta">
					{date && <span className="suno-lyrics-sheet__variant-date">{date}</span>}
					<span className="suno-lyrics-sheet__card-chevron" aria-hidden="true">
						{expanded ? '▾' : '▸'}
					</span>
				</span>
			</button>

			{expanded && (
				<div className="suno-lyrics-sheet__card-body">
					<textarea
						className="suno-lyrics-sheet__variant-textarea"
						value={value}
						onChange={(e) => onEdit(e.target.value)}
						rows={10}
					/>
					<div className="suno-lyrics-sheet__card-actions">
						<ExtendLineButton
							value={value}
							modelId={modelId}
							price={extendPrice}
							onAppend={(line) => onEdit(`${value.replace(/\s+$/, '')}\n${line}`)}
						/>
						<button
							type="button"
							className="suno-sheet-btn suno-sheet-btn--primary suno-sheet-btn--compact"
							onClick={onUse}
						>
							Использовать
						</button>
					</div>
				</div>
			)}
		</div>
	)
}
